<script setup>
import { ref, computed, inject, onMounted } from "vue";
import closeIcon from "@/assets/icon/close.svg";
import { appHapticFeedback, toastFail } from "@/utils/common";
import { useConfigStore } from "@/store/config";
import { useUserStore } from "@/store/user";
import { useI18n } from "vue-i18n";
import { updateProfile } from "@/api/user";
import { useTonConnectStore } from "@/store/tonConnect";

const { t } = useI18n();
const $swal = inject("$swal");
const config = useConfigStore();
const userStore = useUserStore();
const tonConnectStore = useTonConnectStore();
const wallAddress = ref(null);
const withdraw_mode = computed(() => config.withdraw_mode);
const fieldValue = ref(null);
const wallTag = ref(null);
const isLoading = ref(false);
const show = ref(false);
const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  address: {
    type: String,
    default: "",
  }
});
onMounted(() => {
  switch (withdraw_mode.value) {
    case "cash":
      fieldValue.value = columnsCash[0].value;
      break;
    case "token":
      fieldValue.value = columnsToken[0].value;
      break;
    default:
      fieldValue.value = withdraw_mode.value;
  }
});
const emit = defineEmits(["close"]);

const onClose = () => {
  // 清空输入框内容
  wallAddress.value = "";
  wallTag.value = "";
  emit("close");
  appHapticFeedback();
};
const columnsCash = [
  { text: "PayPal", value: "PayPal" },
  { text: "Western Union", value: "Western Union" },
  { text: "Cash App", value: "Cash App" },
  { text: "Bank Card", value: "Bank Card" },
  { text: "Zelle", value: "Zelle" },
  { text: "Venmo", value: "Venmo" },
  { text: "Phone", value: "Phone" },
  { text: "Bitcoin", value: "Bitcoin" },
  { text: "USDT", value: "USDT" }
];
const columnsToken = [
  { text: "BNB Smart Chain", value: "BNB Smart Chain" },
];

const columns = computed(() => {
  switch (withdraw_mode.value) {
    case "cash":
      return columnsCash;
    case "token":
      return columnsToken;
  }
});
const save = async () => {
  appHapticFeedback();
  if (withdraw_mode.value != 'ton' && (wallAddress.value == "" || wallAddress.value == null)) {
    return;
  }

  // ============================================================================
  //  币安链地址格式验证（当 withdraw_mode 为 token/Binance 时）
  // ============================================================================
  if (withdraw_mode.value === "token" || fieldValue.value === "BNB Smart Chain") {
    const address = (wallAddress.value || "").trim();
    if (!isValidBinanceAddress(address)) {
      toastFail($swal, t("message.WalletBscError"));
      return;
    }
  }

  isLoading.value = true;
  try {
    // let res = await checkWalletOwner(
    //   {
    //     wallet_address: wallAddress.value,
    //     wallet_tag: wallTag.value
    //   }
    // );
    // console.log(res);
    // if (res.code != 200) {
    //   //请输入正确币安地址
    //   toastFail($swal, t("message.WalletOwnerError"));
    //   return;
    // }
    if (withdraw_mode.value == 'ton') {
      tonConnectStore.TonConnect.disconnect();
      await updateProfile({
        payment_type: "",
        payment_info: ""
      });
      userStore.setPaymentInfo("", "")
      onClose();
      return;
    }
    // 统一使用 updateProfile 接口更新钱包信息
    await updateProfile({
      payment_type: withdraw_mode.value,
      payment_info: JSON.stringify({
        address: wallAddress.value,
        tag: wallTag.value
      })
    });
    userStore.setPaymentInfo({
      address: wallAddress.value,
      tag: wallTag.value
    }, withdraw_mode.value);
    wallAddress.value = "";
    wallTag.value = "";
    onClose();
  } catch (error) {
    console.error("Save error:", error);
    toastFail($swal, t("message.save_error"));
    // 保存失败时也清空输入框
    wallAddress.value = "";
    wallTag.value = "";
  } finally {
    isLoading.value = false;
  }
};
const showPopup = () => {
  if (withdraw_mode.value == "XRP" || withdraw_mode.value == "ton") {
    return;
  }
  show.value = true;
};
const onConfirm = ({ selectedValues }) => {
  fieldValue.value = selectedValues[0];
  show.value = false;
};
const onCancel = () => {
  show.value = false;
};
const walletAddressPlaceholder = computed(() => config.wallet_tip);

// ============================================================================
//  币安链地址格式验证
// ============================================================================
/**
 * 验证币安链（BSC/BEP20）地址格式
 * 格式要求：0x 开头的 42 位十六进制字符串
 * @param {string} address - 待验证的地址
 * @returns {boolean} 是否为有效的币安链地址格式
 */
const isValidBinanceAddress = (address) => {
  if (!address || typeof address !== "string") {
    return false;
  }

  // 去除首尾空格
  const trimmedAddress = address.trim();

  // 必须以 0x 开头
  if (!trimmedAddress.startsWith("0x")) {
    return false;
  }

  // 总长度必须为 42（0x + 40 位十六进制）
  if (trimmedAddress.length !== 42) {
    return false;
  }

  // 验证后 40 位是否为有效的十六进制字符
  const hexPart = trimmedAddress.slice(2);
  const hexRegex = /^[0-9a-fA-F]{40}$/;
  return hexRegex.test(hexPart);
};
</script>

<template>
  <div class="wallet-config" v-show="props.show">
    <div class="wallet-config__container">
      <!-- Header -->
      <div v-if="config.withdraw_mode != 'ton'">
        <header class="wallet-config__header">
          <h2 class="wallet-config__title">{{ $t("message.Receivingwallet") }}</h2>
          <button class="wallet-config__close" @click="onClose">
            <img :src="closeIcon" alt="close" />
          </button>
        </header>
        <!-- Manual Input Section -->
        <section class="wallet-config__section">
          <div class="wallet-config__input-group">
            <input type="text" class="wallet-config__input wallet-config__input__v1" :value="fieldValue" readonly
              @click="showPopup" />
            <textarea class="wallet-config__input" :placeholder="walletAddressPlaceholder" v-model="wallAddress" />
            <input v-if="withdraw_mode == 'XRP'" type="text" class="wallet-config__input wallet-config__input__v1"
              placeholder="MEMO" v-model="wallTag" />
          </div>
        </section>
      </div>
      <div class="close-icon" @click="onClose" v-if="config.withdraw_mode == 'ton'">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" :fill="config.themes_color">
          <path
            d="M12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22ZM12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20ZM12 10.5858L14.8284 7.75736L16.2426 9.17157L13.4142 12L16.2426 14.8284L14.8284 16.2426L12 13.4142L9.17157 16.2426L7.75736 14.8284L10.5858 12L7.75736 9.17157L9.17157 7.75736L12 10.5858Z">
          </path>
        </svg>
      </div>
      <!-- Save Button -->
      <div class="address-text">{{ props.address }}</div>
      <button v-if="!isLoading" :style="{ marginTop: config.withdraw_mode == 'ton' ? '0' : '24px' }"
        class="wallet-config__button wallet-config__button--save" @click="save">
        {{ config.withdraw_mode == 'ton' ? $t("message.disconnect") : $t("message.save") }}
      </button>
      <div v-else :style="{ marginTop: config.withdraw_mode == 'ton' ? '0' : '24px' }" class="wallet-config__button
        wallet-config__button--save">
        <div class="spinner"></div>
      </div>
    </div>

    <van-popup v-model:show="show" round position="bottom" :style="{ height: '40%' }">
      <van-picker :columns="columns" show-toolbar :confirm-button-text="$t('message.Confirm')"
        :cancel-button-text="$t('message.Cancel')" @confirm="onConfirm" @cancel="onCancel" @change="onChange" />
    </van-popup>
  </div>
</template>

<style lang="scss" scoped>
.wallet-config {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(5px);
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;

  &__container {
    position: relative;
    width: 90vw;
    background: #ffffff;
    padding: 24px;
    border-radius: 12px;
    border: 1px solid #e0e0e0;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  }

  &__header {
    position: relative;
    margin-bottom: 24px;
  }

  &__title {
    font-weight: bold;
    font-size: 6vw;
    color: #333333;
    margin: 0;
  }

  &__close {
    position: absolute;
    top: 50%;
    right: 0;
    transform: translateY(-50%);
    width: 3vh;
    height: 3vh;
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;

    img {
      width: 100%;
      height: 100%;
      filter: invert(0);
    }
  }

  &__section {
    margin-top: 24px;
  }

  &__label {
    color: #666666;
    font-size: 16px;
    margin: 0 0 12px;
    font-weight: normal;
  }

  &__input-group {
    position: relative;
    margin-bottom: 12px;
  }

  &__input {
    width: 100%;
    height: 15vh;
    padding: 3vw;
    border: 1px solid #d0d0d0;
    border-radius: 4px;
    font-size: 16px;
    box-sizing: border-box;
    background-color: #f8f8f8;
    color: #333333;

    &::placeholder {
      color: #999999;
    }

    &:focus {
      outline: none;
      border-color: v-bind("config.themes_color");
      background-color: #ffffff;
    }
  }

  &__input__v1 {
    margin-bottom: 1vh;
    width: 100%;
    height: 6vh;
    padding: 1vw 3vw;

  }

  &__tip {
    color: #666666;
    font-size: 3.5vw;
  }

  &__paste {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    padding: 8px 12px;
    background-color: #f0f0f0;
    border: none;
    border-radius: 4px;
    font-size: 14px;
    color: #333333;
    cursor: pointer;

    &:hover {
      background-color: #e0e0e0;
    }
  }

  &__button {
    width: 100%;
    height: 8vh;
    line-height: 8vh;
    border: none;
    border-radius: 8px;
    font-weight: bold;
    font-size: 20px;
    color: #fff;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    transition: opacity 0.2s;
    background-color: v-bind("config.themes_color");

    &:active {
      opacity: 0.7;
    }

    svg {
      width: 24px;
      height: 24px;
      margin-right: 8px;
    }

    &--connect {
      margin-top: 12px;
      background-color: #f0f0f0;
      color: #333333;
    }

    &--save {
      margin-top: 24px;
    }
  }

  &__disconnect {
    position: absolute;
    bottom: 10vw;
    left: 0;
    width: 100%;
    height: 8vh;
    line-height: 8vh;
    text-align: center;
    background-color: #f0f0f0;
    color: #333333;
  }

  .address-text {
    color: #333333;
    font-size: 4.5vw;
    margin-bottom: 12px;
    text-align: center;
  }
}

.close-icon {
  position: absolute;
  top: 1vw;
  right: 2vw;
  width: 7vw;
  height: 7vw;
}

.spinner {
  width: 24px;
  height: 24px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top: 3px solid #ffffff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}
</style>
